import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import {
	createNativeEmbedChildClient,
	type OnReadContentCallback,
} from '@atlassian/native-embeds-core/services/client/child/main';
import type { NativeEmbedBridgeClientQueryParams } from '@atlassian/native-embeds-core/services/client/iframe-integration/main';
import {
	NativeEmbedCoreFeature,
	type NativeEmbedFeature,
} from '@atlassian/native-embeds-core/types/features/main';

import { fg } from '@confluence/feature-gating';

/**
 * One reference-counted native-embed child client per iframe bridge identity.
 *
 * The Rovo content bridge keeps a single handler per `childClientId:command` key, and `destroy()`
 * tears down every handler for that id. A second client — or an early destroy — would clobber
 * handlers (notably `read-content`) that other mounted consumers still need. So the client is owned
 * here, not by any hook consumer: consumers lease it and the last release destroys it. See the
 * shared-client invariants in AGENTS.md.
 */

export type NativeEmbedChildClient = ReturnType<typeof createNativeEmbedChildClient>;

export type CreateNativeEmbedChildClient = typeof createNativeEmbedChildClient;

export type NativeEmbedChildClientLease = {
	client: NativeEmbedChildClient;
	release: () => void;
};

/**
 * Which surface answers a `read-content` command. The editor is authoritative for the page it has
 * open (including a view-only live doc, which has no renderer body), so `editor` beats `renderer`.
 * Resolving by source rather than registration order stays deterministic while both are briefly
 * mounted during a mode or route transition.
 */
export type ReadPageContentSource = 'renderer' | 'editor';

export type ReadPageContent = {
	contentId: string;
	source: ReadPageContentSource;
	getPageAdf: () => string | Promise<string>;
};

type ReadPageContentRegistration = ReadPageContent & { id: number };

const SOURCE_PRIORITY: Record<ReadPageContentSource, number> = {
	renderer: 0,
	editor: 1,
};

type SharedNativeEmbedChildClient = NativeEmbedBridgeClientQueryParams & {
	client: NativeEmbedChildClient;
	consumerCount: number;
};

let sharedClient: SharedNativeEmbedChildClient | null = null;
let registrations: ReadPageContentRegistration[] = [];
let nextRegistrationId = 0;

const resolveReadPageContentOwner = (): ReadPageContentRegistration | undefined =>
	registrations.reduce<ReadPageContentRegistration | undefined>(
		(owner, candidate) =>
			owner && SOURCE_PRIORITY[candidate.source] < SOURCE_PRIORITY[owner.source]
				? owner
				: candidate,
		undefined,
	);

const setupReadContentDispatch = (client: NativeEmbedChildClient): void => {
	const dispatch = async () => {
		const owner = resolveReadPageContentOwner();

		if (!owner) {
			return undefined;
		}

		const { contentId, getPageAdf } = owner;
		const defaultResult = {
			contentType: 'embeddedPage' as const,
			embeddedPageBody: '',
			contentId,
		};

		try {
			const embeddedPageBody = await getPageAdf();

			if (resolveReadPageContentOwner()?.id !== owner.id) {
				// Owner changed while we awaited (e.g. the embed navigated away) — don't answer the
				// current page with the previous owner's content.
				return defaultResult;
			}

			return {
				...defaultResult,
				embeddedPageBody,
			};
		} catch {
			return defaultResult;
		}
	};

	client.onReadContent(dispatch as OnReadContentCallback);
};

const disposeSharedClient = (): void => {
	sharedClient?.client.destroy();
	sharedClient = null;
	registrations = [];
};

const leaseSharedClient = (
	ownedClient: SharedNativeEmbedChildClient,
): NativeEmbedChildClientLease => {
	ownedClient.consumerCount += 1;
	let released = false;

	return {
		client: ownedClient.client,
		release: () => {
			if (released) {
				return;
			}
			released = true;

			if (sharedClient !== ownedClient) {
				return;
			}

			ownedClient.consumerCount -= 1;
			if (ownedClient.consumerCount === 0) {
				disposeSharedClient();
			}
		},
	};
};

/** Leases the shared client for `identity`, creating it on first acquire. */
export const acquireSharedNativeEmbedChildClient = (
	identity: NativeEmbedBridgeClientQueryParams,
	createClient: CreateNativeEmbedChildClient = createNativeEmbedChildClient,
): NativeEmbedChildClientLease => {
	const { childClientId, parentClientId } = identity;

	if (sharedClient) {
		if (
			sharedClient.childClientId === childClientId &&
			sharedClient.parentClientId === parentClientId
		) {
			return leaseSharedClient(sharedClient);
		}

		disposeSharedClient();
	}

	const client = createClient(
		getBrowserBridgeAPI(),
		childClientId,
		parentClientId,
		new Set<NativeEmbedFeature>([
			NativeEmbedCoreFeature.ContentReady,
			NativeEmbedCoreFeature.NotifyHostClick,
		]),
	);
	sharedClient = { childClientId, parentClientId, client, consumerCount: 0 };

	if (fg('maui_remixable_page_native_embeds')) {
		setupReadContentDispatch(client);
	}

	return leaseSharedClient(sharedClient);
};

/**
 * Registers a candidate read-content owner and returns its unregister. Registrations live here, not
 * on the client, so a provider can register before the client exists and unmounting one consumer
 * can't disturb another.
 */
export const registerReadPageContent = (readPageContent: ReadPageContent): (() => void) => {
	nextRegistrationId += 1;
	const registration: ReadPageContentRegistration = {
		...readPageContent,
		id: nextRegistrationId,
	};
	registrations = [...registrations, registration];

	return () => {
		registrations = registrations.filter(({ id }) => id !== registration.id);
	};
};

export const resetSharedNativeEmbedChildClientForTesting = (): void => {
	disposeSharedClient();
};
